// ── V155 → V156: 오늘의 작업 — 내용 · 마감일 · 담당자 ─────────────────────────────
// 항목 칸 (모두 선택): note 내용 · owner 담당자 · due 'YYYY-MM-DD' (마감일, 날짜만)
//   · from { meetingId, taskId, title, date } — 회의에서 넘어온 테스크 (V156, utils/meetingLink.js)
//   · dueTime 'HH:MM'은 V155에서 잠깐 쓰던 칸. 남아 있으면 보여만 주고 새로 넣지는 않는다
//   · 빈 칸은 아예 지운다 — Firestore는 undefined 값을 받지 않는다
import { fmtDay } from './workCalc';

export const NOTE_MAX = 1000;
export const OWNER_MAX = 30;
export const TASK_MAX = 200;
const META_KEYS = ['note', 'owner', 'due', 'dueTime'];

export const isDateKey = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));
const isTime = (s) => /^\d{2}:\d{2}$/.test(String(s || ''));

/** 입력값 정리 — 비어 있거나 모양이 틀린 칸은 뺀다 */
export function cleanMeta(m = {}) {
  const out = {};
  const note = String(m.note || '').replace(/\r\n/g, '\n').trim().slice(0, NOTE_MAX);
  const owner = String(m.owner || '').replace(/\s+/g, ' ').trim().slice(0, OWNER_MAX);
  if (note) out.note = note;
  if (owner) out.owner = owner;
  if (isDateKey(m.due)) out.due = m.due;
  if (isTime(m.dueTime)) out.dueTime = m.dueTime;
  return out;
}

/** 항목에 내용 · 마감일 · 담당자를 덮어쓴다 (빈 칸은 지움). 다른 칸은 그대로 */
export function applyMeta(item, meta = {}) {
  const base = { ...(item || {}) };
  for (const k of META_KEYS) delete base[k];
  delete base.cat;   // V155 '회의 주제' 구분은 V156에서 회의 탭으로 옮겼다
  return { ...base, ...cleanMeta(meta) };
}

/** 'YYYY-MM-DD'에서 n일 뒤 */
export function addDaysKey(key, n) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * 마감일 글자 — '오늘' · '내일' · '11월 3일 (화)' / '今天' · '明天' · '11月3日 星期二'
 * lang 'bi'는 한국어와 중국어가 다를 때만 ' / '로 같이 쓴다. today가 없으면 늘 날짜로
 */
export function fmtDue(it, lang = 'ko', today = '') {
  if (!it || (!it.due && !it.dueTime)) return '';
  if (lang === 'bi') {
    const ko = fmtDue(it, 'ko', today);
    const zh = fmtDue(it, 'zh', today);
    return ko === zh ? ko : `${ko} / ${zh}`;
  }
  const zh = lang === 'zh';
  let day = '';
  if (it.due) {
    if (today && it.due === today) day = zh ? '今天' : '오늘';
    else if (today && it.due === addDaysKey(today, 1)) day = zh ? '明天' : '내일';
    else if (today && it.due === addDaysKey(today, -1)) day = zh ? '昨天' : '어제';
    else {
      const sameYear = !today || it.due.slice(0, 4) === today.slice(0, 4);
      day = fmtDay(it.due, zh ? 'zh' : 'ko', !sameYear);
    }
  }
  return [day, it.dueTime || ''].filter(Boolean).join(' ');
}

/**
 * 마감 상태 — 'over' 지남 · 'today' 오늘까지 · '' 넉넉함/없음
 * listDate: 시간만 적힌 옛 항목이 가리키는 날 (목록 날짜)
 */
export function dueState(it, { done = false, today = '', listDate = '', now = new Date() } = {}) {
  if (done || !it || (!it.due && !it.dueTime)) return '';
  const day = it.due || listDate || today;
  if (!isDateKey(day)) return '';
  const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const nowHm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  if (day < nowKey) return 'over';
  if (day > nowKey) return '';
  if (it.dueTime && it.dueTime < nowHm) return 'over';
  return 'today';
}

/** 담당자 이름 모으기 — 직원 목록 먼저, 그다음 지금 목록 · 회의 테스크 · 체크한 사람 · 내역 (중복 없이) */
export function ownerSuggestions({ employees = [], items = [], workChecks = {}, history = [], meetings = [], me = '' } = {}) {
  const staff = new Set();
  const others = new Set();
  const clean = (s) => String(s || '').replace(/\s+/g, ' ').trim();
  for (const e of employees || []) { const v = clean(e && e.active !== false ? e.name : ''); if (v && v.length <= OWNER_MAX) staff.add(v); }
  const add = (s) => { const v = clean(s); if (v && v.length <= OWNER_MAX && !staff.has(v)) others.add(v); };
  for (const i of items || []) add(i && i.owner);
  for (const m of (meetings || []).slice(0, 30)) for (const t of (m && m.tasks) || []) add(t && t.owner);
  for (const k of Object.keys(workChecks || {})) add(workChecks[k] && workChecks[k].by);
  for (const h of (history || []).slice(0, 30)) for (const i of (h && h.items) || []) { add(i && i.owner); add(i && i.by); }
  add(me);
  const byName = (a, b) => a.localeCompare(b);
  return [...[...staff].sort(byName), ...[...others].sort(byName)];
}

/** 목록에서 한 항목 옮기기 — from → to (배열 새로 만듦) */
export function moveItem(list, from, to) {
  const arr = Array.isArray(list) ? [...list] : [];
  if (from < 0 || from >= arr.length) return arr;
  const t = Math.max(0, Math.min(arr.length - 1, to));
  if (t === from) return arr;
  const [x] = arr.splice(from, 1);
  arr.splice(t, 0, x);
  return arr;
}
